import { Action, combineReducers, configureStore } from '@reduxjs/toolkit';
import {
  persistStore,
  persistReducer,
  FLUSH,
  REHYDRATE,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER,
  PersistConfig,
} from 'redux-persist';
import storage from 'redux-persist/lib/storage/session';
import { setupListeners } from '@reduxjs/toolkit/dist/query';
import hardSet from 'redux-persist/lib/stateReconciler/hardSet';

import appReducer from '../app/appSlice';
import userReducer from '../user/userSlice';
import categoryReducer from '../category/categorySlice';
import productReducer from '../product/productSlice';
import masterProductReducer from '../masterProduct/masterProductSlice';
import orderDetailReducer from '../order/orderDetailSlice';
import inquiryDetailReducer from '../inquiry/inquiryDetailSlice';

import {
  optionTypeApi,
  categoryApi,
  optionApi,
  editingGroupApi,
  commonInfoApi,
  paperGroupApi,
  uploadS3Api,
  productMasterApi,
  editorConfigApi,
  channelProductAssortmentApi,
  channelCommonInfoApi,
  productThemeApi,
  channelProductApi,
  optionValueAPI,
  dropdownsApi,
  userApi,
  holidayApi,
  warehouseApi,
  channelGroupsApi,
  regionAPI,
} from '../queries';

const rootReducer = combineReducers({
  user: userReducer,
  app: appReducer,
  category: categoryReducer,
  product: productReducer,
  masterProduct: masterProductReducer,
  orderDetail: orderDetailReducer,
  inquiryDetail: inquiryDetailReducer,
  [categoryApi.reducerPath]: categoryApi.reducer,
  [optionTypeApi.reducerPath]: optionTypeApi.reducer,
  [optionApi.reducerPath]: optionApi.reducer,
  [editingGroupApi.reducerPath]: editingGroupApi.reducer,
  [commonInfoApi.reducerPath]: commonInfoApi.reducer,
  [paperGroupApi.reducerPath]: paperGroupApi.reducer,
  [uploadS3Api.reducerPath]: uploadS3Api.reducer,
  [productMasterApi.reducerPath]: productMasterApi.reducer,
  [editorConfigApi.reducerPath]: editorConfigApi.reducer,
  [channelProductAssortmentApi.reducerPath]: channelProductAssortmentApi.reducer,
  [channelProductApi.reducerPath]: channelProductApi.reducer,
  [channelCommonInfoApi.reducerPath]: channelCommonInfoApi.reducer,
  [productThemeApi.reducerPath]: productThemeApi.reducer,
  [optionValueAPI.reducerPath]: optionValueAPI.reducer,
  [dropdownsApi.reducerPath]: dropdownsApi.reducer,
  [holidayApi.reducerPath]: holidayApi.reducer,
  [userApi.reducerPath]: userApi.reducer,
  [warehouseApi.reducerPath]: warehouseApi.reducer,
  [channelGroupsApi.reducerPath]: channelGroupsApi.reducer,
  [regionAPI.reducerPath]: regionAPI.reducer,
});

const persistConfig: PersistConfig<RootState, Action<any>> = {
  key: 'root',
  storage,
  stateReconciler: hardSet,
  // ignore the RTK Query
  blacklist: [
    categoryApi.reducerPath,
    optionTypeApi.reducerPath,
    optionApi.reducerPath,
    editingGroupApi.reducerPath,
    commonInfoApi.reducerPath,
    paperGroupApi.reducerPath,
    uploadS3Api.reducerPath,
    productMasterApi.reducerPath,
    editorConfigApi.reducerPath,
    channelProductAssortmentApi.reducerPath,
    channelCommonInfoApi.reducerPath,
    productThemeApi.reducerPath,
    channelProductApi.reducerPath,
    optionValueAPI.reducerPath,
    dropdownsApi.reducerPath,
    userApi.reducerPath,
    warehouseApi.reducerPath,
    regionAPI.reducerPath,
  ],
};

const persistedReducer = persistReducer(persistConfig, rootReducer) as any;

export const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) => [
    ...getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    }).concat([
      categoryApi.middleware,
      optionTypeApi.middleware,
      optionApi.middleware,
      editingGroupApi.middleware,
      commonInfoApi.middleware,
      paperGroupApi.middleware,
      uploadS3Api.middleware,
      productMasterApi.middleware,
      editorConfigApi.middleware,
      channelProductAssortmentApi.middleware,
      channelCommonInfoApi.middleware,
      productThemeApi.middleware,
      channelProductApi.middleware,
      optionValueAPI.middleware,
      dropdownsApi.middleware,
      holidayApi.middleware,
      userApi.middleware,
      warehouseApi.middleware,
      channelGroupsApi.middleware,
      regionAPI.middleware,
    ]),
  ],
});

setupListeners(store.dispatch);

export const persistor = persistStore(store);

// Infer the `RootState` and `AppDispatch` types from the store itself
export type RootState = ReturnType<typeof store.getState>;
// Inferred type: {posts: PostsState, comments: CommentsState, users: UsersState}
export type AppDispatch = typeof store.dispatch;
